// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');

  var DashboardTile = require('./Izenda.DashboardTile');
  var CommonFilterField = require('./Izenda.CommonFilterField');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var DashboardGridConstants = require('logic/data/Izenda.DashboardGridConstants');
  var trackable = require('share/tracking/Izenda.Trackable');
  var GridLayoutUtils = require('share/utils/Izenda.GridLayoutUtils')(
    DashboardGridConstants.NUMBER_COLS,
    DashboardGridConstants.DEFAULT_TILE_WIDTH,
    DashboardGridConstants.DEFAULT_TILE_HEIGHT
  );
  var constants = require('logic/data/Izenda.DashboardConstants');
  var AccessModel = require('logic/models/Izenda.Access');
  var uiState = require('logic/Izenda.UiState');
  var ACCESS_RIGHT = require('logic/data/Izenda.AuthorizationConstants').ACCESS_RIGHT;
  var DashboardPartDrillDownInfo = require('./Izenda.DashboardPartDrillDownInfo');
  var CrossFilteringDetail = require('../Izenda.CrossFilteringDetail');
  var PillDefinition = require('../Izenda.PillDefinition');
  var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var FUNCTION_GROUP_NAME = require('logic/data/Izenda.ReportPartFieldConstants').FUNCTION.GROUP.NAME;
  var SubscriptionDetails = require('logic/models/Izenda.SubscriptionDetails');

  const {
    find,
    omitBy,
    findIndex,
    indexOf,
    last,
    reject,
    concat,
    clone,
    compact,
    defaults,
    defaultsAll,
    without,
    toNumber,
    sortBy,
    flatMap,
    filter,
    isEmpty,
    map,
    forEach,
    omit,
    uniqueId,
    invoke,
    isNil,
    isEqual,
    flow,
    negate,
    invokeMap,
    get
  } = require('lodash/fp');

  function DashboardDetail(data) {
    var accessModel = data ? data.accessModel : {};
    var props = {
      saveAs: false,
      dashboard: {
        dashboarPartDrillDowns: [],
        crossFilteringDetails: [],
        commonFilterFields: [], // array of CommonFilterField's objects.
        accesses: [],
        subscriptions: [], // It's a array of SubscriptionDetails object.
        dashboardParts: [],
        name: constants.DASHBOARD_DEFAULT_NAME,
        description: null,
        isGlobal: false,
        categoryTypeName: null,
        categoryId: null,
        categoryName: null,
        subCategoryId: null,
        subCategoryName: null,
        tenantId: null,
        backgroundColor: '',
        imageUrl: null,
        stretchImage: false,
        id: null,
        state: 0,
        inserted: false,
        version: null,
        created: null,
        createdBy: null,
        createdById: null,
        modified: null,
        modifiedBy: null,
        showFilterDescription: false,
        owner: uiState.currentUser.fullName,
        ownerId: uiState.currentUser.id,
        lastViewed: null,
        movable: false,
        deletable: false,
        copyable: false,
        isNewDashboard: function() {
          return !String.isGuidFormat(this.id);
        },
        activeDashboardParts: function() {
          return filter({ isDelete: false }, this.dashboardParts);
        },
        reportPartDashboardParts: function() {
          return filter('isReportPartType', this.activeDashboardParts);
        },
        fullScreenDashboardPart: function() {
          return find({ isFullScreenMode: true }, this.activeDashboardParts);
        },
        transparentDashboardGrid: function() {
          return !!(this.backgroundColor || this.imageUrl);
        },
        accessPriority: ACCESS_RIGHT.FullAccess
      },
      accessModel: new AccessModel(accessModel),
      updatedAccess: function() {
        return this.accessModel.accessList;
      }
    };
    this.preset = null;
    m.extendObservable(trackable(this), props);
    data && this.populateData(data);

    this.cleanDirty();
  }

  /**
   * Check Dirty for Dashboard Detail
   * @returns {type}
   */
  DashboardDetail.prototype.checkDirty = function() {
    var dirtyDashboardTiles = filter(function(dashboardTile) {
      return dashboardTile.checkDirty();
    }, this.dashboard.dashboardParts);

    return this.isDirty || !isEmpty(dirtyDashboardTiles) || this.accessModel.checkDirty();
  };

  /**
   * Clean Dirty for Dashboard Detail
   * @param {type} ) {
   */
  DashboardDetail.prototype.cleanDirty = m.action(function() {
    // Reset dirty dashboard detail
    this.resetDirty();

    // Reset dirty dashboard parts
    forEach(function(dashboardPart) {
      dashboardPart.cleanDirty();
    }, this.dashboard.dashboardParts);

    // Clean Dirty access model
    this.accessModel.cleanDirty();
  });

  /**
   * Populate data
   * @param  {Object} dashboard detail object
   */
  DashboardDetail.prototype.populateData = m.action(function(data) {
    data = data || {};
    this.saveAs = data.saveAs;
    this.readDashboardDefinition(data.dashboard);
  });

  /**
   * Populate data to dashboard model from dashboard definition model
   * @param  {Object} dashboard detail object
   */
  DashboardDetail.prototype.readDashboardDefinition = m.action(function(definition, commonFilters) {
    if (isNil(definition)) {
      return;
    }
    var newParts = map(function(item) {
      return new DashboardTile(item);
    }, definition.dashboardParts);

    this.dashboard.commonFilterFields = definition.commonFilterFields;
    this.dashboard.accesses = definition.accesses;
    this.dashboard.subscriptions = definition.subscriptions;
    this.dashboard.dashboardParts.replace(newParts);

    this.readCommonFilters(definition.commonFilterFields, commonFilters);
    this.dashboard.name = definition.name;
    this.dashboard.description = definition.description;
    this.dashboard.categoryId = definition.categoryId;
    this.dashboard.categoryName = definition.categoryName;
    this.dashboard.subCategoryId = definition.subCategoryId;
    this.dashboard.subCategoryName = definition.subCategoryName;
    this.dashboard.tenantId = definition.tenantId;
    this.dashboard.backgroundColor = definition.backgroundColor;
    this.dashboard.imageUrl = definition.imageUrl;
    this.dashboard.stretchImage = definition.stretchImage;
    this.dashboard.id = definition.id;
    this.dashboard.state = definition.state;
    this.dashboard.inserted = definition.inserted;
    this.dashboard.version = definition.version;
    this.dashboard.created = definition.created;
    this.dashboard.createdBy = definition.createdBy;
    this.dashboard.createdById = definition.createdById;
    this.dashboard.modified = definition.modified;
    this.dashboard.modifiedBy = definition.modifiedBy;
    this.dashboard.showFilterDescription = definition.showFilterDescription;
    this.dashboard.owner = definition.owner;
    this.dashboard.ownerId = definition.ownerId;
    this.dashboard.accessPriority = definition.accessPriority || ACCESS_RIGHT.FullAccess;
    this.dashboard.lastViewed = definition.lastViewed;
    this.dashboard.isGlobal = definition.isGlobal;
    this.dashboard.copyable = definition.copyable;
    this.dashboard.movable = definition.movable;
    this.dashboard.deletable = definition.deletable;
  });

  /**
   * return data objet for save
   * @method buildDataForSaving
   * @return {[type]}           [description]
   */
  DashboardDetail.prototype.buildDataForSaving = function() {
    return {
      saveAs: this.saveAs,
      dashboard: this.getDashboardDefinition(true)
    };
  };

  /**
   * return dashboard definition
   * @method getDashboardDefinition
   * @return {[type]}               [description]
   */
  DashboardDetail.prototype.getDashboardDefinition = function(forSaving) {
    var dashboardDefinition = omit(
      ['categoryId', 'subCategoryId', 'dashboardParts', 'subscriptions', 'commonFilterFields'],
      this.dashboard
    );
    var dashboard = this.dashboard;

    dashboardDefinition.categoryId = String.isGuidFormat(dashboard.categoryId) ? dashboard.categoryId : null;
    dashboardDefinition.subCategoryId = String.isGuidFormat(dashboard.subCategoryId) ? dashboard.subCategoryId : null;

    // Update for dashboard parts.
    dashboardDefinition.dashboardParts = flow(
      filter(function(dashboardTile) {
        return negate(isNil)(dashboardTile.type) || dashboardTile.contentFromPreset;
      }),
      invokeMap('prepareDataForSave'),
      m.toJS,
      compact
    )(dashboard.dashboardParts);

    dashboardDefinition.commonFilterFields = invokeMap('buildDataForSave', m.toJS(dashboard.commonFilterFields));

    if (this.isDirty && this.dashboard.state === ObjectStateConstants.NONE) {
      dashboardDefinition.state = ObjectStateConstants.UPDATE;
    }

    // Update for dashboard subscription
    dashboardDefinition.subscriptions = this.updateSubscription(forSaving);
    // update for dashboard access here.
    dashboardDefinition.accesses = this.updatedAccess;
    // update tenantId:
    dashboardDefinition.tenantId = uiState.getTenantId();

    return dashboardDefinition;
  };

  /**
   * toggle show filter description
   * @method toggleShowFilterDescription
   * @param  {[type]}                 value [description]
   */
  DashboardDetail.prototype.toggleShowFilterDescription = m.action(function() {
    this.dashboard.showFilterDescription = !this.dashboard.showFilterDescription;
    this.markDirty();
  });

  /**
   * read preset data and generate dashboard parts
   * @method readPreset
   * @param  {JSON}   preset Preset config
   */
  DashboardDetail.prototype.readPreset = m.action(function(preset) {
    ensure(isEmpty(this.dashboard.dashboardParts), 'already have dashboard parts');
    this.preset = clone(preset);
    this.dashboard.dashboardParts.replace(
      map(
        function(tile) {
          var data = defaults(tile, {
            id: uniqueId('dashboardTile_'),
            dashboardId: get('id', this.dashboard),
            state: ObjectStateConstants.INSERT,
            dashboardPartContent: {
              contentFromPreset: true
            }
          });

          return new DashboardTile(data);
        }.bind(this),
        preset.tiles
      )
    );
    this.markDirty();
  });

  /**
   * add mew tile in dashboard
   * @method addDashboardTile
   * @param  {Object}         position Location of new tiles
   * @return {DashboardTile}           New DashboardTile just created
   */
  DashboardDetail.prototype.addDashboardTile = m.action(function addDashboardTile(x, y, newDashboardPartDefinition) {
    var newDimension = GridLayoutUtils.getDimension(this.dashboard.activeDashboardParts, x, y);
    var data = defaultsAll([
      newDimension,
      {
        id: uniqueId('dashboardTile_'),
        dashboardId: get('id', this.dashboard),
        state: ObjectStateConstants.INSERT
      },
      newDashboardPartDefinition
    ]);
    forEach(function(filterItem) {
      filterItem.id = null;
      filterItem.state = ObjectStateConstants.INSERT;
    }, data.filters);

    var newDashboardPart = new DashboardTile(data);

    newDashboardPart.markDirty();

    this.dashboard.dashboardParts.push(newDashboardPart);
    this.markDirty();
    return newDashboardPart;
  });

  /**
   * copy dashboardPart
   * @method copyDashboardPart
   * @param  {DashboardTile}          dashboardPart Original DashboardTile
   * @return {DashboardTile}                 New Dashboard Tile just created
   */
  DashboardDetail.prototype.copyDashboardPart = m.action(function(dashboardPart) {
    var dashboardPartDefinition = dashboardPart.prepareDataForSave();

    var data = defaults(dashboardPartDefinition, {
      title: String.generateNewName(dashboardPart.title, this.dashboard.activeDashboardParts, get('title'), '()')
    });

    return this.addDashboardTile(null, null, data);
  });

  /**
   * delete dashboardPart
   * @method deleteDashboardPart
   * @param  {object}            dashboardPart Dashboard Part need delete
   */
  DashboardDetail.prototype.deleteDashboardPart = m.action(function(dashboardPart) {
    if (dashboardPart.isNew) {
      this.dashboard.dashboardParts.remove(dashboardPart);
    } else {
      dashboardPart.delete();
    }
    this.markDirty();
  });

  /**
   * rename dashboard Part, if newName is empty use default name
   * @method renameDashboardPart
   * @param  {dashboardPart}     dashboardPart Dashboard Part object neeed modify
   * @param  {String}            newName       New Name of dashboard part
   */
  DashboardDetail.prototype.renameDashboardPart = m.action(function(dashboardPart, newName) {
    if (isEmpty(newName)) {
      var defaultName = dashboardPart.getDefaultName;
      var otherDashboardParts = without([dashboardPart], this.dashboard.activeDashboardParts);

      newName = String.generateNewName(defaultName, otherDashboardParts, get('title'));
    }

    dashboardPart.setTitle(newName);

    this.updatePillTooltip(dashboardPart);
    this.markDirty();
  });

  /**
   * validate dashboard name not duplicate with other
   * @method validateNewNameDashboardPart
   * @param  {dashboardPart}     dashboardPart Dashboard Part object neeed modify
   * @param  {String}            newName       New Name of dashboard part
   * @return {Promise}                         Promise for validation
   */
  DashboardDetail.prototype.validateNewNameDashboardPart = function(dashboardPart, newName) {
    if (!isEmpty(newName)) {
      var otherObject = without([dashboardPart], this.dashboard.activeDashboardParts);

      if (String.checkDuplicate(newName, otherObject, get('title'))) {
        return Promise.resolve(DashboardGridConstants.DUPLICATE_MESSAGE);
      }
    }
    this.markDirty();
    return Promise.resolve('');
  };

  /**
   * set type and defaultName for dashboard part
   * @method setType
   * @param  {dashboardPart}    dashboardPart Dashboard Part object neeed modify
   * @param  {object}           type           Dashboard Part Type
   * @param  {object}           report         Report Definition object
   * @param  {object}           reportPart     Report Part Definition Part Type
   */
  DashboardDetail.prototype.setType = m.action(function(dashboardPart, type, report, reportPart) {
    ensure(!isNil(type) && !isNil(dashboardPart), 'Must have dashboardPart and type');
    dashboardPart.setType(type, report, reportPart);
    this.switchDashboardPartSide(dashboardPart, !reportPart);
    this.renameDashboardPart(dashboardPart);
    this.markDirty();
  });

  /**
   * switch dashboardPart, if new side is back side, switch other dashbboard part to front side
   * @param: {DashboardTile} dashboardPart     Current dashboard part need to switch side
   * @param: {boolean}       isBackSide        is New side back side, if fall will switch from previous side
   */
  DashboardDetail.prototype.switchDashboardPartSide = m.action(function(dashboardPart, isBackSide) {
    dashboardPart.toggleSide(isBackSide);
    if (dashboardPart.isBackSide) {
      flow(without([dashboardPart]), filter('isBackSide'), forEach(invoke('toggleSide')))(
        this.dashboard.activeDashboardParts
      );
    }
  });

  /**
   * Update dashboard name in save popup
   * @param {string} name
   */
  DashboardDetail.prototype.changeName = m.action(function(name) {
    this.dashboard.name = name;
    this.markDirty();
  });

  /**
   * Update dashboard description
   * @param {string} value
   */
  DashboardDetail.prototype.setDescription = m.action(function(value) {
    this.dashboard.description = value;
    this.markDirty();
  });

  /**
   * Update background image
   * @param {String} imageUrl
   * @param {Boolean} stretchImage
   */
  DashboardDetail.prototype.setBackgroundImage = m.action(function(imageUrl, stretchImage) {
    this.dashboard.imageUrl = imageUrl;
    this.dashboard.stretchImage = stretchImage;
    this.markDirty();
  });

  /**
   * Update background color
   * @param {String} imageUrl
   * @param {Boolean} stretchImage
   */
  DashboardDetail.prototype.setBackgroundColor = m.action(function(color) {
    this.dashboard.backgroundColor = color;
    this.markDirty();
  });

  /**
   * Handle when change dashboard's category type, reload list category.
   * @param {GUI|string} value [is 0 or 1]
   * @param {string} name [new category type name (global or local category)]
   */
  DashboardDetail.prototype.changeCategoryType = m.action(function(value) {
    this.dashboard.isGlobal = value.isGlobal;
    this.markDirty();
  });

  /**
   * Handle when change dashboard's category, reload list sub-category
   * @param {GUI|string} id [new category id]
   * @param {string} name [new category name]
   * @return {promise} [promise result from API]
   */
  DashboardDetail.prototype.changeCategory = m.action(function(id, name) {
    this.dashboard.categoryId = id || null;
    this.dashboard.categoryName = name || '';
    this.markDirty();
  });

  /**
   *  Update dashboard's sub-category in save popup
   */
  DashboardDetail.prototype.updateSubCategory = m.action(function(id, name) {
    this.dashboard.subCategoryId = id || null;
    this.dashboard.subCategoryName = name || '';
    this.markDirty();
  });

  /**
   *  Get data for validate dashboard name
   */
  DashboardDetail.prototype.getInitialData = function() {
    var result = {
      id: null,
      name: this.dashboard.name,
      isGlobal: !!this.dashboard.isGlobal,
      categoryId: String.isGuidFormat(this.dashboard.categoryId) ? this.dashboard.categoryId : null,
      categoryName: this.dashboard.categoryName,
      subCategoryId: String.isGuidFormat(this.dashboard.subCategoryId) ? this.dashboard.subCategoryId : null,
      subCategoryName: this.dashboard.subCategoryName,
      tenantId: uiState.getTenantId()
    };

    return result;
  };

  /**
   *  read common filter field from api
   * @method readCommonFilters
   * @param  {[type]}          result [description]
   * @return {[type]}                 [description]
   */
  DashboardDetail.prototype.readCommonFilters = m.action(function(result, commonFilters) {
    var commonFilterFields = (this.dashboard.commonFilterFields = flow(
      map(function(commonFilterField) {
        var filterItem = new CommonFilterField(commonFilterField);

        // Populate filter values to common filter.
        if (!isNil(commonFilters) && !isEmpty(commonFilters)) {
          var matchedFilter = find(function(item) {
            return filterItem.position === toNumber(item.positionString);
          }, commonFilters);

          matchedFilter && (filterItem.value = matchedFilter.value);
        }

        return filterItem;
      }),
      sortBy(['position'])
    )(result));

    var filterMapping = flatMap(function(commonFilterField) {
      return map(function(filterField) {
        return {
          filterFieldId: filterField.filterFieldId,
          commonFilterField: commonFilterField
        };
      }, commonFilterField.filterFields);
    }, commonFilterFields);

    flow(
      flatMap(flow(get('filters'), invoke('peek'))),
      forEach(function(filterField) {
        var commonFilterField = flow(find(['filterFieldId', filterField.filterFieldId]), get('commonFilterField'))(
          filterMapping
        );

        if (isNil(commonFilterField)) {
          filterField.isCommon = false;
        } else {
          filterField.isCommon = true;
          var commonFilterFieldValue = commonFilterField.value;
          if (!isNil(commonFilterFieldValue)) {
            filterField.value = commonFilterFieldValue;
          }
          commonFilterField.filterFieldsMapping.push(filterField);
        }
      })
    )(this.dashboard.reportPartDashboardParts);
  });

  /**
   *  Update subcription
   */
  DashboardDetail.prototype.updateSubscription = m.action(function(forSaving) {
    const clearnedSubscriptions = map(function(sub) {
      const subcription = new SubscriptionDetails(sub);
      if (subcription.state === ObjectStateConstants.INSERT) {
        subcription.id = null;
      }
      return subcription;
    }, this.dashboard.subscriptions);

    if (forSaving) {
      this.dashboard.subscriptions = clearnedSubscriptions;
    }

    return invokeMap('omitData', m.toJS(clearnedSubscriptions));
  });

  /**
   * Sync Subscription.
   * @param  {Object} subscribedInstances
   * @param  {Boolean} isSubscribe
   */
  DashboardDetail.prototype.syncSubscriptions = m.action(function(subscribedInstances, isSubscribe) {
    var cloneSubscribedInstances = subscribedInstances.toJS();
    var subscriptions = this.dashboard.subscriptions;
    var hasChange = false;

    for (var i = subscriptions.length - 1; i >= 0; i--) {
      var index = findIndex(function(instance) {
        return instance.id === subscriptions[i].id;
      }, cloneSubscribedInstances);

      if (index !== -1) {
        // Update
        if (!isEqual(subscriptions[i], cloneSubscribedInstances[index])) {
          subscriptions[i] = cloneSubscribedInstances[index];
          hasChange = true;
        }
        cloneSubscribedInstances.splice(index, 1);
      } else if (subscriptions[i].isSubscription === isSubscribe) {
        // remove
        subscriptions.splice(i, 1);
        hasChange = true;
      }
    }

    // add
    forEach(function(item) {
      subscriptions.push(item);
    }, cloneSubscribedInstances);

    hasChange && this.markDirty();
  });

  /**
   * Update dashboard owner
   * @param  {array} dashboardOwner   list selected dasahboard owner
   */
  DashboardDetail.prototype.updateDashboardOwner = m.action(function(dashboardOwner) {
    var owner = last(dashboardOwner) || {};
    this.dashboard.owner = owner.fullName;
    this.dashboard.ownerId = owner.id;
    this.markDirty();
  });

  /**
   * update common filter when click subscription on dashboard list
   * @param  {array}
   */
  DashboardDetail.prototype.updateCommonFilter = m.action(function(data) {
    this.dashboard.commonFilterFields = map(function(item) {
      return new CommonFilterField(item);
    }, data);
  });

  /**
   * Update drilldown in Dashboard tile to list this.dashboard.dashboarPartDrillDowns
   * If drilldown of dashboardPart already exist -> replace with the new info.
   * If drillDownData.expandedLevel <= 0 (back to All level) -> remove drillDown.
   */
  // @flow
  DashboardDetail.prototype.updateDrillDownCollection = function(dashboardPart: DashboardTile): void {
    // if report part is not cross filtering --> goodbye, no future cross filter actions need to be run.
    if (!this.isCrossFiltering(dashboardPart)) {
      return;
    }
    let dashboardPartDrillDownInfo = new DashboardPartDrillDownInfo(dashboardPart);
    let currentDrillDowns = get('dashboarPartDrillDowns', this.dashboard);
    let index = indexOf(find(['dashboardPart.id', dashboardPart.id], currentDrillDowns), currentDrillDowns);

    let isDrillDown = invoke('reportPart.reportPartContent.isDrillingDown', dashboardPart);
    if (index >= 0) {
      isDrillDown ? currentDrillDowns.splice(index, 1, dashboardPartDrillDownInfo) : currentDrillDowns.splice(index, 1);
    } else {
      isDrillDown && currentDrillDowns.push(dashboardPartDrillDownInfo);
    }
  };

  /**
   * Remove related dashboardPart DrillDown out of collection.
   * @param dashboardPart: DashboardTile
   */
  // @flow
  DashboardDetail.prototype.removeDrillDownInCollection = function(dashboardPart: DashboardTile): void {
    // if report part is not cross filtering --> goodbye, no future cross filter actions need to be run.
    if (!this.isCrossFiltering(dashboardPart)) {
      return;
    }
    let currentDrillDowns = get('dashboarPartDrillDowns', this.dashboard);
    let drillDown = this.getRelatedDrillDown(dashboardPart);

    let index = indexOf(drillDown, currentDrillDowns);
    index >= 0 && currentDrillDowns.splice(index, 1);
  };

  /**
   * Find related Drill down.
   * @param dashboardPart: DashboardTile
   */
  // @flow
  DashboardDetail.prototype.getRelatedDrillDown = function(dashboardPart: DashboardTile): DashboardPartDrillDownInfo {
    if (!this.isCrossFiltering(dashboardPart)) {
      return null;
    }
    return flow(get('dashboarPartDrillDowns'), find(['dashboardPart.id', dashboardPart.id]))(this.dashboard);
  };

  /**
   * Get all dashboard parts, which has cross filtering with @param drilledDownDashboardPart
   * @param drilledDownDashboardPart
   * return collection of related crossfiltering dashboard part,
   * which has the same report id with currentDownDashboardPart's report id.
   */
  // @flow
  DashboardDetail.prototype.getRelatedCrossFilteringDashboardParts = function(
    dashboardPart: DashboardTile
  ): Array<DashboardTile> {
    // if report part is not cross filtering --> goodbye, no future cross filter actions need to be run.
    if (!this.isCrossFiltering(dashboardPart)) {
      return [];
    }
    // get dashboardparts with has same reportId and isCrossFiltering
    let dashboardPartCandidates = flow(
      get('dashboardParts'),
      filter(part => {
        return part.reportId === dashboardPart.reportId && this.isCrossFiltering(part);
      }),
      reject(['id', dashboardPart.id])
    )(this.dashboard);

    return dashboardPartCandidates;
  };

  /**
   * Get filtering (cross filtering) for dashbard part.
   * return cross filtering array for a dashboard part. [{key: '', value: ''}]
   * @param dashboardPart: DashboardTile
   */
  // @flow
  DashboardDetail.prototype.getCrossFilteringForDashboardPart = function(
    dashboardPart: DashboardTile,
    hasCrossFiltering: Boolean
  ): Array {
    // if report part is not cross filtering --> goodbye, no future cross filter actions need to be run.

    if (!hasCrossFiltering && !this.isCrossFiltering(dashboardPart)) {
      return [];
    }

    // get dashboardparts with has same reportId and isCrossFiltering
    let dashboardPartCandidates = flow(
      get('dashboarPartDrillDowns'),
      filter(
        dashboarPartDrillDown =>
          dashboarPartDrillDown.dashboardPart.reportId === dashboardPart.reportId &&
          this.isCrossFiltering(dashboarPartDrillDown.dashboardPart)
      ),
      reject(['dashboardPart.id', dashboardPart.id])
    )(this.dashboard);

    let crossFiltering = [];
    forEach(dashboardPartDrillDown => {
      let filters = this.getCrossFiltering(dashboardPart, dashboardPartDrillDown.dashboardPart);
      crossFiltering = concat(crossFiltering, filters);
    }, dashboardPartCandidates);
    return crossFiltering;
  };

  /**
   * Get Alias function in filtering dashboard part which related to driling down dashboard part.
   * @param {DashboardTile} dashboardPart: filtering dashboard part (which is filtering).
   * @param {DashboardTile} drillingDownDashboardPart: drilling down dashboard part.
   */
  DashboardDetail.prototype.getCrossFiltering = function(
    dashboardPart: DashboardTile,
    drillingDownDashboardPart: DashboardTile
  ): Array {
    let crossfilteringInfoes = invoke('reportPart.getCrossFilteringFilter', drillingDownDashboardPart);
    let crossFilteringFilters = [];
    forEach(crossFilteringInfo => {
      if (!isNil(crossFilteringInfo.field)) {
        let { key, fieldId } = this.scanRelatedField(dashboardPart, crossFilteringInfo.field);
        let filterItem = {
          isCrossFilter: true,
          fieldId: fieldId,
          key: key,
          value: crossFilteringInfo.value,
          dataFormat: get('field.reportPartElm.properties.dataFormattings.format.formatId', crossFilteringInfo)
        };
        filterItem = omitBy(isNil, filterItem);
        crossFilteringFilters.push(filterItem);
      }
    }, crossfilteringInfoes);

    return crossFilteringFilters;
  };

  /**
   * get field alias which applied function in dashboard part and related to input field
   * @param {DashboardTile} dashboardPart: filtering dashboardPart.
   * @param {any} field: field
   */
  DashboardDetail.prototype.scanRelatedField = function(dashboardPart: DashboardTile, field: any): any {
    let groupFunctionName = FUNCTION_GROUP_NAME;
    let reportPartElm = get('reportPartElm', field);
    let functionName = get('reportPartElm.properties.dataFormattings.functionInfo.name', field);
    // If function is Group --> act it like a normal field
    functionName === groupFunctionName && (functionName = null);
    let { sourceAlias, fieldId, fieldName, fieldNameAlias, relationshipId } = reportPartElm.field;
    let key = `[${sourceAlias}].[${fieldName}]`;
    let finalFieldId = null;
    let finalKey = '';
    // Process finding related function.
    if (!isNil(functionName)) {
      let elementCandidates = flow(
        get('reportPart.elementInfo'),
        filter(
          info =>
            get('element.field.sourceAlias', info) === sourceAlias &&
            get('element.field.fieldName', info) === fieldName &&
            get('element.field.relationshipId', info) === relationshipId
        )
      )(dashboardPart);

      // search by function.
      if (!isEmpty(elementCandidates)) {
        forEach(elementCandidate => {
          let elementFunctionName = get('element.properties.dataFormattings.functionInfo.name', elementCandidate);
          elementFunctionName === groupFunctionName && (elementFunctionName = null);
          if (elementFunctionName === functionName) {
            finalKey = isNil(functionName) ? key : get('element.field.fieldNameAlias', elementCandidate);
            return false;
          }
        }, elementCandidates);
      }
    } else {
      // normal field.
      if (isNil(relationshipId) || relationshipId === constants.EMPTY_GUID) {
        finalFieldId = fieldId;
        finalKey = null;
      } else {
        finalKey = key;
      }
    }

    return {
      key: finalKey,
      fieldId: finalFieldId
    };
  };

  /**
   * Logic to update related pills when doing drilldown on a report part.
   * expected that dashboard.dashboarPartDrillDowns is already changed.
   * @param dashboardPart: DashboardTile
   */
  // @flow
  DashboardDetail.prototype.onDrillDownUpdatePill = function(dashboardPart: DashboardTile): void {
    let relatedDrillDownInfo = flow(get('dashboarPartDrillDowns'), find(['dashboardPart.id', dashboardPart.id]))(
      this.dashboard
    );

    let relatedPill = this.findRelatedPill(dashboardPart);

    // drillDown is removed --> remove Pill
    !isNil(relatedPill)
      ? isNil(relatedDrillDownInfo) && this.removePill(get('report.id', dashboardPart), get('id', dashboardPart))
      : !isNil(relatedDrillDownInfo) && this.addPill(relatedDrillDownInfo);
  };

  /**
   * Remove a pill whith related to reportId and dashboardId
   * After removing pill, if the crossfilter which contain pill is empty -> remove that cross filtering also.
   * @param reportId: string
   * @param dashboardPartId: string.
   */
  // @flow
  DashboardDetail.prototype.removePill = function(reportId: string, dashboardPartId: string): void {
    let existingCrossFiltering = get('crossFilteringDetails', this.dashboard);
    let relatedCrossFiltering = find(['reportId', reportId], existingCrossFiltering);

    if (!isNil(relatedCrossFiltering)) {
      let pillIndex = indexOf(
        find(['parentId', dashboardPartId], relatedCrossFiltering.pillDefinitions),
        relatedCrossFiltering.pillDefinitions
      );
      pillIndex >= 0 && relatedCrossFiltering.pillDefinitions.splice(pillIndex, 1);

      if (isEmpty(relatedCrossFiltering.pillDefinitions)) {
        let crossFilteringIndex = indexOf(relatedCrossFiltering, existingCrossFiltering);
        existingCrossFiltering.splice(crossFilteringIndex, 1);
      }
    }
  };

  /**
   * Add new Pill
   * @param drillDownInfo: DashboardPartDrillDownInfo
   */
  // @flow
  DashboardDetail.prototype.addPill = function(drillDownInfo: DashboardPartDrillDownInfo): void {
    let findCrossFiltering = this.findRelatedCrossFiltering(drillDownInfo.dashboardPart);

    const dashboardPartId = get('dashboardPart.id', drillDownInfo);
    let pillDefinition = new PillDefinition(
      dashboardPartId,
      get('dashboardPart.reportPart', drillDownInfo),
      drillDownInfo.dashboardPart.editingTitle
    );
    pillDefinition.parentId = dashboardPartId;

    if (isNil(findCrossFiltering)) {
      let { name: reportName, id: reportId } = drillDownInfo.dashboardPart.report;
      findCrossFiltering = new CrossFilteringDetail(reportId, reportName, [pillDefinition]);
      let existingCrossFiltering = get('crossFilteringDetails', this.dashboard);
      existingCrossFiltering.push(findCrossFiltering);
    } else {
      findCrossFiltering.pillDefinitions.push(pillDefinition);
    }
  };

  /**
   * Update the exiting pill which related to drillDownInfo, drill down or drill up an exsixting pill.
   * @param drillDownInfo: DashboardPartDrillDownInfo
   */
  // @flow
  DashboardDetail.prototype.updatePill = function(drillDownInfo: DashboardTile): void {
    let findCrossFiltering = flow(
      get('crossFilteringDetails'),
      find(['report.id', drillDownInfo.dashboardPart.report.id])
    )(this.dashboard);
    let newPillDefinition = new PillDefinition(get('dashboardPart.reportPart', drillDownInfo));
    let existingPillDefinition = find(
      ['dashboardId', get('dashboardPart.id', drillDownInfo)],
      findCrossFiltering.pillDefinitions
    );
    let existingPillIndex = indexOf(existingPillDefinition, findCrossFiltering.pillDefinitions);
    findCrossFiltering.pillDefinitions.splice(existingPillIndex, 1, newPillDefinition);
  };

  /**
   * find related Cross Filtering
   * @param dashboardPart: DashbordTile
   */
  // @flow
  DashboardDetail.prototype.findRelatedCrossFiltering = function(dashboardPart: DashboardTile): CrossFilteringDetail {
    let reportId = get('report.id', dashboardPart);
    let relatedCrossFiltering =
      reportId && flow(get('crossFilteringDetails'), find(['reportId', dashboardPart.report.id]))(this.dashboard);
    return relatedCrossFiltering;
  };

  /**
   * find associated Pill which related to dashboard part.
   * @param dashboardPart: DashboardTile
   * return PillDefinition
   */
  // @flow
  DashboardDetail.prototype.findRelatedPill = function(dashboardPart: DashboardTile): PillDefinition {
    let crossFiltering = this.findRelatedCrossFiltering(dashboardPart);

    let relatedPill = flow(get('pillDefinitions'), find(['parentId', dashboardPart.id]))(crossFiltering);

    return relatedPill;
  };

  /**
   * update Pill's tooltip
   * @param dashboardPart: DashboardTile
   */
  // @flow
  DashboardDetail.prototype.updatePillTooltip = function(dashboardPart: DashboardTile): void {
    let relatedPill = this.findRelatedPill(dashboardPart);
    !isNil(relatedPill) && (relatedPill.tooltip = dashboardPart.editingTitle);
  };
  /**
   * Function to identify if that dashboard part is Cross filtering or not?
   * @param dashboardPart: check if dash board tile is cross filtering.
   */
  // @flow
  DashboardDetail.prototype.isCrossFiltering = function(dashboardPart: DashboardTile): boolean {
    return get('reportPart.reportPartContent.isCrossFiltering', dashboardPart);
  };

  /**
   * Action when click remove pill in crossfiltering bar.
   */
  // @flow
  DashboardDetail.prototype.onClearDrillDown = function(dashboardPart: DashboardTile): void {
    let reportPartContent = get('reportPart.reportPartContent', dashboardPart);
    reportPartContent && invoke('resetDrilldownInfo', reportPartContent);
  };

  // @flow
  DashboardDetail.prototype.removeAllCrossFilterings = function(): void {
    let array = this.dashboard.crossFilteringDetails;
    !isEmpty(array) && array.splice(0, array.length);
  };

  // @flow
  DashboardDetail.prototype.removeAllDrillDowns = function(): void {
    let array = this.dashboard.dashboarPartDrillDowns;
    !isEmpty(array) && array.splice(0, array.length);
  };

  /**
   * Calculate color for cross filterings.
   */
  // @flow
  DashboardDetail.prototype.calculateCrossFilteringColors = function(): void {
    let crossfilteringDetails = get('crossFilteringDetails', this.dashboard);
    let index = 0;
    forEach(crossfilteringDetail => {
      let alternative = !(index % 2);
      index++;
      forEach(pill => {
        pill.alternative = alternative;
      }, crossfilteringDetail.pillDefinitions);
    }, crossfilteringDetails);
  };

  /**
   * get all report types, which are allowed for crossfiltering.
   */
  // @flow
  function allowedCrossFilteringReportTypes(): Array<number> {
    let reportPartTypes = ReportDesignerConstants.REPORT_PART_TYPE;
    return [reportPartTypes.Chart, reportPartTypes.Gauge, reportPartTypes.Map];
  }

  /**
   * check if dashboardpart is valid for cross filtering.
   * @param {DashboardTile} dashboardPart dashboard part need to check.
   */
  // @flow
  DashboardDetail.prototype.isAllowedCrossFiltering = function(dashboardPart: DashboardTile): boolean {
    let allowedReportPartTypes = allowedCrossFilteringReportTypes();
    let reportPartType = get('reportPart.reportPartContent.type', dashboardPart);
    return !flow(find(type => type == reportPartType), isNil)(allowedReportPartTypes);
  };

  module.exports = DashboardDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Dashboard/Izenda.DashboardDetail.js